/**
 * 1.81.0 · Servidor local: puente con los comandos de Windows (src-tauri/src/lan.rs). Solo en la app de
 * Windows; en web y Android no hay servidor local (ahí se muestra el cartel de sin sincronización).
 */
import { env } from "@/lib/env";

export const lanAvailable = () => env.platform === "desktop";

export type LanNativeEvent =
  | { kind: "open"; conn: number; addr: string }
  | { kind: "msg"; conn: number; data: string }
  | { kind: "closed"; conn: number }
  | { kind: "client_msg"; data: string }
  | { kind: "client_closed" };

export interface FoundServer { ip: string; port: number; info: string }

async function core() {
  return import("@tauri-apps/api/core");
}

let unlisten: Promise<() => void> | null = null;
const handlers = new Set<(e: LanNativeEvent) => void>();

/** Escucha los avisos de la red (uno solo para toda la app; se reparte a quien lo pida). */
export function onLanEvent(h: (e: LanNativeEvent) => void): () => void {
  handlers.add(h);
  if (!unlisten && lanAvailable()) {
    unlisten = import("@tauri-apps/api/event").then(({ listen }) =>
      listen<LanNativeEvent>("lan", (e) => { for (const x of handlers) x(e.payload); }));
  }
  return () => { handlers.delete(h); };
}

export const lanNative = {
  async serverStart(info: string, port?: number): Promise<number> {
    return (await core()).invoke<number>("lan_server_start", { info, port: port ?? null });
  },
  async serverInfo(info: string) { await (await core()).invoke("lan_server_info", { info }); },
  async serverRunning(): Promise<number | null> { return (await core()).invoke<number | null>("lan_server_running"); },
  async serverStop() { await (await core()).invoke("lan_server_stop"); },
  async serverSend(conn: number, data: string) { await (await core()).invoke("lan_server_send", { conn, data }); },
  async serverClose(conn: number) { await (await core()).invoke("lan_server_close", { conn }); },
  async discover(ms = 1500): Promise<FoundServer[]> { return (await core()).invoke<FoundServer[]>("lan_discover", { ms }); },
  async localIp(): Promise<string> { return (await core()).invoke<string>("lan_local_ip"); },
  async connect(host: string, port: number) { await (await core()).invoke("lan_connect", { host, port }); },
  async clientSend(data: string) { await (await core()).invoke("lan_client_send", { data }); },
  async clientClose() { await (await core()).invoke("lan_client_close"); },
  /** Con el rol de servidor, cerrar la ventana la esconde junto al reloj (el servidor sigue atendiendo). */
  async keepRunning(on: boolean) { await (await core()).invoke("set_close_to_tray", { on }); },
  /** 1.99.3: ¿Windows marcó la red como «Pública»? / dejar entrar a los puestos también ahí (pide permiso). */
  async networkPublic(): Promise<boolean> { return (await core()).invoke<boolean>("lan_network_public"); },
  async fixPublicNetwork(): Promise<void> { await (await core()).invoke("lan_fix_public_network"); },
  /** 1.99.2: la PC servidor no se suspende sola. */
  async keepAwake(on: boolean) { await (await core()).invoke("power_keep_awake", { on }); },
};
